import { ActionReducerMap, createReducer, on } from '@ngrx/store';
import { CellExportActions, NotebookState, NotebookTypes } from '@mode/notebooks/contract';
import { NotebookResource, SessionResource } from '@mode/notebooks/contract';
import * as notebookCellAction from '../notebook-cell/notebook-cell.actions';
import * as notebookAction from './notebook.actions';
export const notebookFeatureKey = 'notebook';
import * as NotebookActions from './notebook.actions';
/***
 * This block may be reused when migrating related actions to Angular — keeping it for now.
 */
export interface NotebookFeatureState {
  tokens: string[];
  pusherChannelNames: Record<string, string>;
  sessions: Record<string, SessionResource>;
  resources: Record<string, NotebookResource>;
  initialized: Record<string, boolean>;
  states: Record<string, NotebookState>;
  awaitingInitialRun: Record<string, boolean>;
  notebookTypes: Record<string, NotebookTypes.NotebookType>;
  cellTokensByNotebook: Record<string, string[]>;
  importing: boolean;
  importError: string | null;
}

export const notebookInitialState: NotebookFeatureState = {
  tokens: [],
  pusherChannelNames: {},
  sessions: {},
  resources: {},
  initialized: {},
  states: {},
  awaitingInitialRun: {},
  notebookTypes: {},
  cellTokensByNotebook: {},
  importing: false,
  importError: null,
};

export const tokenReducer = createReducer(notebookInitialState.tokens);
export const pusherChannelNamesReducer = createReducer(notebookInitialState.pusherChannelNames);
export const sessionsReducer = createReducer(notebookInitialState.sessions);
export const resourcesReducer = createReducer(notebookInitialState.resources);
export const initializedReducer = createReducer(notebookInitialState.initialized);
export const awaitingInitialRunReducer = createReducer(notebookInitialState.awaitingInitialRun);
export const notebookTypesReducer = createReducer(notebookInitialState.notebookTypes);
export const statesReducer = createReducer(
  notebookInitialState.states,
  on(NotebookActions.refreshSessionReportRun, (state) => {
    const updated: Record<string, NotebookState> = {};
    Object.keys(state).forEach((token) => {
      updated[token] = NotebookState.Loading;
    });
    return {
      ...state,
      ...updated,
    };
  }),

  on(NotebookActions.terminated, (state, { notebookToken, terminationReason }) => {
    if (!notebookToken) return state;

    return {
      ...state,
      [notebookToken]: terminationReason !== 'OBO_USER' ? NotebookState.Error : NotebookState.Loading,
    };
  }),
  on(notebookAction.cancelRun, (state, { notebookToken }) => ({
    ...state,
    [notebookToken]: NotebookState.Cancelling,
  })),

  on(notebookAction.cancelRunSuccess, (state, { notebookToken }) => ({
    ...state,
    [notebookToken]: NotebookState.Cancelled,
  })),

  on(notebookAction.cancelRunComplete, (state, { notebookToken }) => ({
    ...state,
    [notebookToken]: NotebookState.Ready,
  }))
);

export const cellTokensByNotebookReducer = createReducer(
  notebookInitialState.cellTokensByNotebook as Record<string, string[]>,
  on(notebookCellAction.addCellSuccess, (state, { notebookToken, cellResource, nextToken }) => {
    const existingTokens = state[notebookToken] ?? [];
    const newTokens = [...existingTokens];

    if (nextToken) {
      const index = newTokens.indexOf(nextToken);
      if (index >= 0) {
        newTokens.splice(index, 0, cellResource.token);
      } else {
        newTokens.push(cellResource.token);
      }
    } else {
      newTokens.push(cellResource.token);
    }

    return {
      ...state,
      [notebookToken]: newTokens,
    };
  }),
  on(notebookCellAction.destroyCell, (state, { notebookToken, cellToken }) => {
    const current = state[notebookToken] || [];
    return {
      ...state,
      [notebookToken]: current.filter((token) => token !== cellToken),
    };
  }),
  on(notebookCellAction.destroyCellFailure, (state, { notebookToken, cellToken }) => {
    const current = state[notebookToken] || [];
    if (!current.includes(cellToken)) {
      return {
        ...state,
        [notebookToken]: [...current, cellToken],
      };
    }
    return state;
  }),
  // Handle moveCell by updating cell order in notebook
  on(notebookCellAction.moveCell, (state, { notebookToken, cellToken, down }) => {
    const current = state[notebookToken] || [];
    const newTokens = [...current];
    const index = newTokens.indexOf(cellToken);

    if (index >= 0) {
      newTokens.splice(index, 1);
      if (down) {
        newTokens.splice(index + 1, 0, cellToken);
      } else {
        newTokens.splice(index - 1 >= 0 ? index - 1 : 0, 0, cellToken);
      }
    }

    return {
      ...state,
      [notebookToken]: newTokens,
    };
  }),
  // Handle moveCellFailure by reverting to original order
  on(notebookCellAction.moveCellFailure, (state, { notebookToken, cellToken, nextCellToken }) => {
    const current = state[notebookToken] || [];
    const newTokens = [...current];
    const index = newTokens.indexOf(cellToken);

    if (index >= 0) {
      newTokens.splice(index, 1);
      const nextIndex = newTokens.indexOf(nextCellToken);
      newTokens.splice(nextIndex, 0, cellToken);
    }

    return {
      ...state,
      [notebookToken]: newTokens,
    };
  })
);

export const importingReducer = createReducer(
  notebookInitialState.importing,
  on(NotebookActions.importNotebook, () => true),
  on(NotebookActions.importNotebookFailure, () => false),
  on(NotebookActions.importNotebookCompleted, NotebookActions.importNotebookFailed, () => false)
);

export const importErrorReducer = createReducer(
  notebookInitialState.importError,
  on(NotebookActions.importNotebook, () => null),
  on(
    NotebookActions.importNotebookFailure,
    NotebookActions.importNotebookFailed,
    (state, { message }) => message || 'Failed to import notebook'
  ),
  on(NotebookActions.importNotebookCompleted, () => null)
);

export const notebookReducer: ActionReducerMap<NotebookFeatureState> = {
  tokens: tokenReducer,
  pusherChannelNames: pusherChannelNamesReducer,
  sessions: sessionsReducer,
  resources: resourcesReducer,
  initialized: initializedReducer,
  states: statesReducer,
  awaitingInitialRun: awaitingInitialRunReducer,
  cellTokensByNotebook: cellTokensByNotebookReducer,
  notebookTypes: notebookTypesReducer,
  importing: importingReducer,
  importError: importErrorReducer,
};
